import { Component, ElementRef, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EpeFormData, EpeTopicEntry, SUPPORT_TYPE_OPTIONS } from 'app/models/application';
import { TOPIC_GROUPS, TopicGroup } from 'app/models/epe-topics';
import { ApplicationsService } from 'app/services/applications.service';
import { SessionStorage } from 'app/storage/session.storage';
import { switchMap, take } from 'rxjs/operators';
import { FieldError, focusAndScrollToField } from 'app/shared/forms/form-validation.util';

export function arrayMinLength(min: number): ValidatorFn {
  return (c: AbstractControl): ValidationErrors | null => {
    const v = c.value;
    return Array.isArray(v) && v.length >= min ? null : { arrayMinLength: { min } };
  };
}

@Component({
  selector: 'app-epe-application-form',
  templateUrl: './epe-application-form.component.html',
  styleUrls: ['./epe-application-form.component.scss']
})
export class EpeApplicationFormComponent implements OnInit {
  form!: FormGroup;
  submitting    = false;
  formSubmitted = false;
  errorMessage  = '';
  fieldErrors: FieldError[] = [];

  draftId    : string | null = null;
  savingDraft = false;
  draftSaved  = false;

  expandedGroups    = new Set<string>();
  expandedSubGroups = new Set<string>();

  cvFilename     = '';
  cvUploading    = false;
  cvUploadError  = '';

  readonly MIN_TOPICS         = 1;
  readonly MAX_TOPICS         = 5;

  readonly SUPPORT_TYPE_OPTIONS = SUPPORT_TYPE_OPTIONS;

  readonly SDO_OPTIONS: { group: string; options: string[] }[] = [
    { group: 'European', options: [
      'ASD-STAN',
      'CEN – European Committee for Standardisation',
      'CENELEC – The European Electronic Committee for Standardisation',
      'ETSI',
      'SBS - Small Business Standards',
    ]},
    { group: 'National Standards Bodies', options: [
      'Austria: ASI – Austrian Standards Institute',
      'Austria: OVE - Österreichischer Verband für Elektrotechnik',
      'Belgium: NBN – Bureau de Normalisation',
      'Bulgaria: BDS – Bulgarian Institute for Standardisation',
      'Croatia: HZN – Croation Standards Institute',
      'Cyprus: CYS – Cyprus Organisation for Standardisation',
      'Czech Republic: UNMZ – Czech Office for Standards, Metrology and Testing',
      'Denmark: DS – Dansk Standard',
      'Estonia: EVS – Estonian Centre for Standardisation',
      'Finland: SFS – Finnish Standards Association',
      'France: AFNOR – Association Francaise de normalization',
      'Germany: DIN – Deutsches Institut für Normung e.V.',
      'Germany: DKE - Deutsche Kommission Elektrotechnik Elektronik und Informationstechnik in DIN und VDE',
      'Greece: ELOT – Hellenic Organisation for Standardisation',
      'Hungary: MSZT - Magyar Szabványügyi Testület',
      'Iceland: IST – Icelandic Standards',
      'Ireland: NSAI – National Standards Authority of Ireland',
      'Israel: SII – Standards Institution of Israel',
      'Italy: CEI – Comitato Elettrotecnico Italiano',
      'Italy: UNI – Ente Nazionale Italiano di Unificazione',
      'Latvia: LVS – Standardisation, Accreditation and Metrology Centre, Latvian Standard',
      'Lithuania: LST – Lithuanian Standards Board',
      'Luxembourg: ILNAS – Institut luxembourgeois de la normalisation, de l\'accréditation, de la sécurité et qualité des produits et services',
      'Malta: MCCAA – Malta Competition and Consumer Affairs Authority',
      'Moldova, Republic of: ISM – Institute for Standardisation of Moldova',
      'Montenegro: ISME – Institute for Standardisation of Montenegro',
      'Netherlands: NEN – Nederlands Normalisatie-institut',
      'North Macedonia: ISRM – Standardisation Institute of the Republic of Macedonia',
      'Norway: SON – Standards Norway',
      'Norway: NEK - Norsk Elektroteknisk Komite (The Norwegian Electrotechnical Committee)',
      'Poland: PKN – Polski Komitet Normalizacyjny (Polish Committee for Standardization)',
      'Portugal: IPQ - Instituto Português da Qualidade',
      'Romania: ASRO - Asociaţia de Standardizare din România (Romanian Standards Association)',
      'Slovakia: SOSMT – Slovak Office of Standards, Metrology and Testing',
      'Slovenia: SIST – Slovenian Institute for Standardisation',
      'Spain: UNE – Asociación Española de Normalización',
      'Sweden: SEK - Svensk Elstandard',
      'Sweden: SIS – Swedish Institute for Standards',
      'Switzerland: SNV – Swiss Association for Standardisation',
      'Switzerland: CES - Electrosuisse',
      'Turkey: TSE – Turkish Standards Institution',
      'Ukraine: DTSU - State Enterprise "Ukrainian Scientific, Research and Training Center on Standardization, Certification and Quality Problems"',
      'United Kingdom: BSI – British Standards Institution',
    ]},
    { group: 'International', options: [
      'DMTF',
      'IEC – International Electrotechnical Commission',
      'IEEE - Institute of Electrical and Electronics Engineers',
      'IETF - Internet Engineering Task Force',
      'ISO - International Organization for Standardization',
      'ITU - International Telecommunication Union',
      'OGF – Open Grid Forum',
      'OneM2M',
      'UN/CEFACT – Trade Facilitation and E-business',
      'W3C - World Wide Web Consortium',
      'Other – Please specify',
    ]},
  ];

  readonly ROLE_OPTIONS: string[] = [
    'Chair',
    'Co-editor',
    'Convenor',
    'Delegate',
    'Editor',
    'Expert',
    'Head of delegation',
    'Liaison',
    'Project Manager',
    'Secretary (TC)',
    'Secretary (SC)',
    'Secretary (WG)',
    'Other (Please specify)',
  ];

  readonly YEARS_OPTIONS: string[] = ['1','2','3','4','5','6','7','8','9','10+'];

  readonly AVAILABILITY_OPTIONS: string[] = [
    'Up to 1 day/month',
    '1-3 days/month',
    '4-6 days/month',
    'More than 6 days/month',
  ];

  readonly SERVICE_OPTIONS: string[] = [
    'Proposal support',
    'Mentoring',
    'Standards Accelerator',
  ];

  readonly LANGUAGE_OPTIONS: string[] = [
    'Bulgarian', 'Croatian', 'Czech', 'Danish', 'Dutch', 'English', 'Estonian',
    'Finnish', 'French', 'German', 'Greek', 'Hungarian', 'Irish', 'Italian',
    'Latvian', 'Lithuanian', 'Maltese', 'Polish', 'Portuguese', 'Romanian',
    'Slovak', 'Slovenian', 'Spanish', 'Swedish',
  ];

  readonly TOPIC_GROUPS: TopicGroup[] = TOPIC_GROUPS;

  constructor(
    private fb: FormBuilder,
    private applicationsService: ApplicationsService,
    private auth: SessionStorage,
    private router: Router,
    private hostRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      topics: this.fb.array([]),
      standardisation_experience: ['no'],
      sdo_member: ['no'],
      standardisation_roles: this.fb.array([]),
      fit_support_approach: ['', [Validators.required, Validators.maxLength(2000)]],
      fit_ri_experience: ['', Validators.maxLength(1500)],
      fit_knowledge_valorisation: ['', Validators.maxLength(1000)],
      fit_additional_expertise: ['', Validators.maxLength(1000)],
      availability: ['', Validators.required],
      preferred_services: [[] as string[], [arrayMinLength(1)]],
      working_languages: [['English'] as string[], [arrayMinLength(1)]],
      time_zone: ['', Validators.required],
      availability_limitations: [''],
      cv: ['', Validators.required],
      confidentiality_declaration: [false, Validators.requiredTrue],
      accuracy_declaration: [false, Validators.requiredTrue],
      data_protection_consent: [false, Validators.requiredTrue],
      public_profile_consent: ['', Validators.required],
      newsletter_subscription: ['', Validators.required],
    });

    this.checkForExistingApplication();
  }

  private checkForExistingApplication(): void {
    const doCheck = (userId: number) => {
      this.applicationsService.getByUser(userId).subscribe({
        next: apps => {
          const epeApp = apps.find(a => a.application_type === 'epe');
          if (!epeApp) return;
          const raw: any = epeApp._id;
          const id = raw?.['$oid'] ?? String(raw);
          if (epeApp.status === 'draft') {
            this.draftId = id;
            this.loadDraft(epeApp.form_data as any || {});
          } else {
            this.router.navigate(['/my-applications']);
          }
        }
      });
    };
    if (this.auth.userId) {
      doCheck(this.auth.userId);
    } else {
      this.auth.auth$.pipe(take(1)).subscribe(info => {
        if (info?.id) doCheck(info.id);
      });
    }
  }

  private resolveTopicMeta(value: string): { label: string; groupLabel: string } {
    for (const group of TOPIC_GROUPS) {
      for (const sub of group.subGroups) {
        if (sub.value === value) return { label: sub.label, groupLabel: group.label };
        const child = sub.children.find(c => c.value === value);
        if (child) return { label: child.label, groupLabel: group.label };
      }
    }
    return { label: value, groupLabel: '' };
  }

  private loadDraft(data: any): void {
    this.form.patchValue({
      standardisation_experience: data.standardisation_experience ?? 'no',
      sdo_member:                 data.sdo_member ?? 'no',
      fit_support_approach:       data.fit_support_approach ?? '',
      fit_ri_experience:          data.fit_ri_experience ?? '',
      fit_knowledge_valorisation: data.fit_knowledge_valorisation ?? '',
      fit_additional_expertise:   data.fit_additional_expertise ?? '',
      availability:               data.availability ?? '',
      preferred_services:         data.preferred_services ?? [],
      working_languages:          data.working_languages ?? ['English'],
      time_zone:                  data.time_zone ?? '',
      availability_limitations:   data.availability_limitations ?? '',
      cv:                         data.cv ?? '',
      confidentiality_declaration: data.confidentiality_declaration ?? false,
      accuracy_declaration:        data.accuracy_declaration ?? false,
      data_protection_consent:     data.data_protection_consent ?? false,
      public_profile_consent:      data.public_profile_consent ?? '',
      newsletter_subscription:     data.newsletter_subscription ?? '',
    });
    if (data.cv && data.cv_filename) this.cvFilename = data.cv_filename;
    for (const t of (data.topics || []) as any[]) {
      const meta = this.resolveTopicMeta(t.value);
      this.topics.push(this.fb.group({
        value:                  [t.value],
        label:                  [meta.label],
        groupLabel:             [meta.groupLabel],
        evidence_of_expertise:  [t.evidence_of_expertise || '', [Validators.required, Validators.maxLength(1000)]],
        primary_support_types:  [t.primary_support_types || [], [arrayMinLength(1)]],
      }));
    }
    if (data.standardisation_experience === 'yes' && data.sdo_member === 'yes') {
      for (const r of (data.standardisation_roles || []) as any[]) {
        this.standardisationRoles.push(this.fb.group({
          sdo: [r.sdo || ''], other_sdo: [r.other_sdo || ''], tc_number: [r.tc_number || ''],
          tc_title: [r.tc_title || ''], roles: [r.roles || ''], years_of_experience: [r.years_of_experience || ''],
        }));
      }
    }
  }

  private buildFormData(): EpeFormData {
    const v = this.form.value;
    return {
      topics: (v.topics as any[]).map(t => ({
        value: t.value,
        evidence_of_expertise: t.evidence_of_expertise,
        primary_support_types: t.primary_support_types,
      }) as EpeTopicEntry),
      standardisation_experience: v.standardisation_experience,
      sdo_member: v.standardisation_experience === 'yes' ? v.sdo_member : 'no',
      standardisation_roles: (v.standardisation_experience === 'yes' && v.sdo_member === 'yes') ? v.standardisation_roles : [],
      fit_support_approach:       v.fit_support_approach,
      fit_ri_experience:          v.fit_ri_experience,
      fit_knowledge_valorisation: v.fit_knowledge_valorisation,
      fit_additional_expertise:   v.fit_additional_expertise,
      availability:               v.availability,
      preferred_services:         v.preferred_services,
      working_languages:          v.working_languages,
      time_zone:                  v.time_zone,
      availability_limitations:   v.availability_limitations,
      cv:                         v.cv,
      confidentiality_declaration: v.confidentiality_declaration,
      accuracy_declaration:        v.accuracy_declaration,
      data_protection_consent:     v.data_protection_consent,
      public_profile_consent:      v.public_profile_consent,
      newsletter_subscription:     v.newsletter_subscription,
    };
  }

  saveDraft(): void {
    this.savingDraft = true;
    const formData: any = this.buildFormData();
    if (this.cvFilename) formData.cv_filename = this.cvFilename;
    const obs$ = (this.draftId
      ? this.applicationsService.updateForm(this.draftId, formData)
      : this.applicationsService.createDraft(this.auth.userId!, 'epe', formData)) as any;
    obs$.subscribe({
      next: (res: any) => {
        if (!this.draftId && res?._id) {
          const raw: any = res._id;
          this.draftId = raw?.['$oid'] ?? String(raw);
        }
        this.draftSaved  = true;
        this.savingDraft = false;
        setTimeout(() => { this.draftSaved = false; }, 3000);
      },
      error: () => { this.savingDraft = false; }
    });
  }

  // ── Validation ───────────────────────────────────────────────────────────

  isFormValid(): boolean {
    return this.getFieldErrors().length === 0;
  }

  getFieldErrors(): FieldError[] {
    const errors: FieldError[] = [];

    if (this.topics.length < this.MIN_TOPICS || this.topics.length > this.MAX_TOPICS) {
      errors.push({ key: 'topics', label: `Select between ${this.MIN_TOPICS} and ${this.MAX_TOPICS} open call topics` });
    }
    this.topics.controls.forEach((t, i) => {
      const label = t.get('label')?.value || `Topic ${i + 1}`;
      if (t.get('evidence_of_expertise')?.invalid) {
        errors.push({ key: `topic-${i}-evidence`, label: `Evidence of expertise — ${label}` });
      }
      if (t.get('primary_support_types')?.invalid) {
        errors.push({ key: `topic-${i}-support`, label: `Primary type of support — ${label}` });
      }
    });

    const exp = this.form.get('standardisation_experience')?.value;
    const member = this.form.get('sdo_member')?.value;
    if (exp === 'yes' && member === 'yes' && this.standardisationRoles.length < 1) {
      errors.push({ key: 'standardisation_roles', label: 'Add at least one standardisation role' });
    }

    const simpleFields: [string, string][] = [
      ['fit_support_approach', 'Standardisation support approach'],
      ['fit_ri_experience', 'Experience with supporting research and innovation projects'],
      ['fit_knowledge_valorisation', 'Evidence of knowledge valorisation experience'],
      ['fit_additional_expertise', 'Additional standardisation expertise'],
      ['availability', 'Indicative availability'],
      ['preferred_services', 'Preferred types of services'],
      ['working_languages', 'Working languages'],
      ['time_zone', 'Time zone'],
      ['cv', 'CV upload'],
      ['confidentiality_declaration', 'Confidentiality declaration'],
      ['accuracy_declaration', 'Accuracy declaration'],
      ['data_protection_consent', 'Data protection consent'],
      ['public_profile_consent', 'Public profile consent'],
      ['newsletter_subscription', 'Newsletter subscription'],
    ];
    for (const [key, label] of simpleFields) {
      if (this.form.get(key)?.invalid) errors.push({ key, label });
    }

    // Safety net: surface any invalid control this method didn't explicitly label.
    if (this.form.invalid && errors.length === 0) {
      for (const key of Object.keys(this.form.controls)) {
        if (this.form.get(key)?.invalid) errors.push({ key, label: key });
      }
    }

    return errors;
  }

  jumpToField(key: string): void {
    focusAndScrollToField(this.hostRef.nativeElement, key);
  }

  // ── Topic picker ─────────────────────────────────────────────────────────

  toggleGroup(label: string): void {
    this.expandedGroups.has(label) ? this.expandedGroups.delete(label) : this.expandedGroups.add(label);
  }

  isGroupExpanded(label: string): boolean { return this.expandedGroups.has(label); }

  toggleSubGroup(value: string): void {
    this.expandedSubGroups.has(value) ? this.expandedSubGroups.delete(value) : this.expandedSubGroups.add(value);
  }

  isSubGroupExpanded(value: string): boolean { return this.expandedSubGroups.has(value); }

  selectTopic(value: string, label: string, groupLabel: string): void {
    const idx = this.topics.controls.findIndex(c => c.get('value')?.value === value);
    if (idx > -1) {
      this.topics.removeAt(idx);
    } else if (this.topics.length < this.MAX_TOPICS) {
      this.topics.push(this.fb.group({
        value: [value],
        label: [label],
        groupLabel: [groupLabel],
        evidence_of_expertise: ['', [Validators.required, Validators.maxLength(1000)]],
        primary_support_types: [[] as string[], [arrayMinLength(1)]],
      }));
    }
  }

  isTopicSelected(value: string): boolean {
    return this.topics.controls.some(c => c.get('value')?.value === value);
  }

  removeTopic(value: string): void {
    const idx = this.topics.controls.findIndex(c => c.get('value')?.value === value);
    if (idx > -1) this.topics.removeAt(idx);
  }

  canSelectMore(): boolean { return this.topics.length < this.MAX_TOPICS; }

  toggleSupportType(topicCtrl: AbstractControl, opt: string): void {
    const ctrl = topicCtrl.get('primary_support_types')!;
    const list: string[] = [...(ctrl.value || [])];
    const i = list.indexOf(opt);
    if (i > -1) list.splice(i, 1); else list.push(opt);
    ctrl.setValue(list);
    ctrl.markAsTouched();
  }

  isSupportSelected(topicCtrl: AbstractControl, opt: string): boolean {
    return ((topicCtrl.get('primary_support_types')?.value as string[]) || []).includes(opt);
  }

  toggleChoice(controlName: string, opt: string): void {
    const ctrl = this.form.get(controlName)!;
    const list: string[] = [...(ctrl.value || [])];
    const i = list.indexOf(opt);
    if (i > -1) list.splice(i, 1); else list.push(opt);
    ctrl.setValue(list);
    ctrl.markAsTouched();
  }

  isChoiceSelected(controlName: string, opt: string): boolean {
    return ((this.form.get(controlName)?.value as string[]) || []).includes(opt);
  }

  // ── FormArray helpers ────────────────────────────────────────────────────

  get topics(): FormArray {
    return this.form.get('topics') as FormArray;
  }

  get standardisationRoles(): FormArray {
    return this.form.get('standardisation_roles') as FormArray;
  }

  addStandardisationRole(): void {
    this.standardisationRoles.push(this.fb.group({
      sdo: [''], other_sdo: [''], tc_number: [''], tc_title: [''], roles: [''], years_of_experience: ['']
    }));
  }

  isOtherSdo(roleGroup: AbstractControl): boolean {
    return roleGroup.get('sdo')?.value === 'Other – Please specify';
  }

  removeStandardisationRole(index: number): void { this.standardisationRoles.removeAt(index); }

  // ── CV upload ────────────────────────────────────────────────────────────

  onCvFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      this.cvUploadError = 'Only PDF files are accepted.';
      return;
    }
    this.cvUploading   = true;
    this.cvUploadError = '';
    this.cvFilename    = '';
    this.form.get('cv')?.setValue('');
    this.applicationsService.uploadCv(file).subscribe({
      next: (res) => {
        this.form.get('cv')?.setValue(res.cv_file_id);
        this.cvFilename   = res.filename;
        this.cvUploading  = false;
      },
      error: (err) => {
        this.cvUploadError = err?.error?.error || 'Upload failed. Please try again.';
        this.cvUploading   = false;
      }
    });
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  onSubmit(): void {
    this.formSubmitted = true;
    this.fieldErrors = this.getFieldErrors();
    if (this.fieldErrors.length) {
      focusAndScrollToField(this.hostRef.nativeElement, this.fieldErrors[0].key);
      return;
    }
    this.submitting = true;
    const formData = this.buildFormData();
    if (this.draftId) {
      this.applicationsService.updateForm(this.draftId, formData).pipe(
        switchMap(() => this.applicationsService.updateStatus(this.draftId!, 'pending', this.auth.userId!))
      ).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    } else {
      this.applicationsService.create(this.auth.userId!, 'epe', formData).subscribe({
        next: () => this.router.navigate(['/my-applications']),
        error: (err: any) => {
          this.errorMessage = err?.error?.message || 'An error occurred whilst submitting. Please try again.';
          this.submitting = false;
        }
      });
    }
  }
}
